import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, Skeleton, toast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  useNoticeSpecTemplatesQuery,
  useNoticeSpecTemplateMutation,
  type NoticeSpecTemplate,
  type NoticeSpecTemplatePayload,
} from '../hooks/useNoticeSpecTemplates';
import { NoticeSpecTemplateFormModal } from '../components/NoticeSpecTemplateFormModal';

interface FormModalState {
  open: boolean;
  mode: 'create' | 'edit';
  editTarget: NoticeSpecTemplate | null;
}

const INITIAL_FORM: FormModalState = { open: false, mode: 'create', editTarget: null };

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * QA-80 (ID80) — 상품정보고시 템플릿 관리 페이지. 템플릿(번호/필수정보명/항목수/등록일) 목록 +
 * 등록/수정/삭제. 상품 등록·수정 폼에서 이 템플릿을 선택해 고시 항목을 prefill 한다.
 */
export const NoticeSpecTemplateListPage = () => {
  const { t } = useTranslation('products');
  const query = useNoticeSpecTemplatesQuery();
  const { create, update, remove } = useNoticeSpecTemplateMutation();

  const [formModal, setFormModal] = useState<FormModalState>(INITIAL_FORM);
  const [deleteTarget, setDeleteTarget] = useState<NoticeSpecTemplate | null>(null);

  const openCreate = () => setFormModal({ open: true, mode: 'create', editTarget: null });
  const openEdit = (template: NoticeSpecTemplate) =>
    setFormModal({ open: true, mode: 'edit', editTarget: template });
  const closeForm = () => setFormModal(INITIAL_FORM);

  const handleSubmit = (payload: NoticeSpecTemplatePayload) => {
    const onError = (error: unknown) =>
      toast.error(friendlyMessage(error, t('products.noticeSpecTemplates.saveError', { defaultValue: '템플릿 저장에 실패했습니다.' })));
    if (formModal.mode === 'create') {
      create.mutate(payload, { onSuccess: closeForm, onError });
    } else if (formModal.editTarget) {
      update.mutate(
        { templateId: formModal.editTarget.templateId, payload },
        { onSuccess: closeForm, onError },
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.templateId, {
      onSuccess: () => setDeleteTarget(null),
      onError: (error) =>
        toast.error(friendlyMessage(error, t('products.noticeSpecTemplates.deleteError', { defaultValue: '템플릿 삭제에 실패했습니다.' }))),
    });
  };

  const items = query.data ?? [];
  const formPending = create.isPending || update.isPending;

  return (
    <PageShell
      title={t('products.noticeSpecTemplates.title', { defaultValue: '상품정보고시 템플릿' })}
      actions={
        <Button size="sm" onClick={openCreate}>
          <Plus size={14} aria-hidden="true" />
          {t('products.noticeSpecTemplates.create', { defaultValue: '템플릿 등록' })}
        </Button>
      }
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('products.noticeSpecTemplates.loadFailed', { defaultValue: '템플릿 목록을 불러오지 못했습니다.' })}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('products.actions.retry', { defaultValue: '재시도' })}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div
          role="status"
          className="flex flex-col items-center gap-2 rounded-lg py-16 text-center"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)', color: 'var(--content-muted)' }}
        >
          <p className="text-[14px]">{t('products.noticeSpecTemplates.empty', { defaultValue: '등록된 고시 템플릿이 없습니다.' })}</p>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-lg"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
        >
          <Table data-testid="notice-spec-template-table">
            <TableHeader style={{ background: 'var(--surface-1)' }}>
              <TableRow>
                <TableHead scope="col" style={{ width: '60px' }}>
                  {t('products.noticeSpecTemplates.cols.no', { defaultValue: '번호' })}
                </TableHead>
                <TableHead scope="col">
                  {t('products.noticeSpecTemplates.cols.name', { defaultValue: '필수정보명' })}
                </TableHead>
                <TableHead scope="col" style={{ width: '100px' }}>
                  {t('products.noticeSpecTemplates.cols.itemCount', { defaultValue: '항목 수' })}
                </TableHead>
                <TableHead scope="col" style={{ width: '120px' }}>
                  {t('products.noticeSpecTemplates.cols.createdAt', { defaultValue: '등록일' })}
                </TableHead>
                <TableHead scope="col" style={{ width: '120px', textAlign: 'right' }}>
                  {t('products.noticeSpecTemplates.cols.actions', { defaultValue: '관리' })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((tpl, idx) => (
                <TableRow key={tpl.templateId} data-testid="notice-spec-template-row">
                  <TableCell className="tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    {items.length - idx}
                  </TableCell>
                  <TableCell className="font-medium" style={{ color: 'var(--content-strong)' }}>
                    {tpl.name}
                  </TableCell>
                  <TableCell className="tabular-nums" style={{ color: 'var(--content-default)' }}>
                    {Object.keys(tpl.items).length}
                  </TableCell>
                  <TableCell className="tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    {formatDate(tpl.createdAt)}
                  </TableCell>
                  <TableCell style={{ textAlign: 'right' }}>
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(tpl)}
                        aria-label={t('products.noticeSpecTemplates.editAria', { name: tpl.name, defaultValue: `${tpl.name} 수정` })}
                      >
                        <Pencil size={14} style={{ color: 'var(--content-muted)' }} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(tpl)}
                        aria-label={t('products.noticeSpecTemplates.deleteAria', { name: tpl.name, defaultValue: `${tpl.name} 삭제` })}
                      >
                        <Trash2 size={14} style={{ color: 'var(--status-danger-fg)' }} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NoticeSpecTemplateFormModal
        open={formModal.open}
        onOpenChange={(open) => { if (!open) closeForm(); }}
        mode={formModal.mode}
        editTarget={formModal.editTarget}
        isPending={formPending}
        onSubmit={handleSubmit}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.noticeSpecTemplates.deleteTitle', { defaultValue: '고시 템플릿 삭제' })}</DialogTitle>
            <DialogDescription>
              {t('products.noticeSpecTemplates.deleteDesc', {
                name: deleteTarget?.name ?? '',
                defaultValue: `"${deleteTarget?.name ?? ''}" 템플릿을 삭제하시겠습니까? 이미 적용된 상품의 고시 정보는 유지됩니다.`,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">{t('products.actions.cancel', { defaultValue: '취소' })}</Button>
            </DialogClose>
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={handleDeleteConfirm}
            >
              {t('products.actions.delete', { defaultValue: '삭제' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
